// Per-version diff for the staff material reader: the partner asked Alex for
// an edit and wants to SEE what changed, not re-read the whole document.
// Block-level LCS over the same safe md dialect the renderer understands,
// word-level inline diff for rewritten prose blocks. Pure functions exported
// for tests.
import { Fragment, type ReactNode } from "react";
import { MarkdownView } from "./DeliverySections";
import { buildDocContext } from "./doc/doc-context";
import { isDocHtml } from "./DocHtml";
import { docPlainText, parseDocHtml } from "./doc/doc-parse";

export type DiffOp =
  | { type: "same"; block: string }
  | { type: "add"; block: string }
  | { type: "del"; block: string }
  | { type: "change"; oldBlock: string; newBlock: string; inline: boolean };

// Mirrors MarkdownView's block semantics: fences (the whole `:::` family,
// ??? folds) stay one block, table/list/quote runs stay together, blank
// lines split paragraphs, single-line constructs (heading, callout,
// divider) stand alone.
/**
 * Authored-HTML documents are segmented by CHAPTER, never by blank lines.
 * Splitting them the markdown way cuts through tags, and each fragment then
 * fails the `<doc>` check and renders as literal tag soup. Chapter granularity
 * is coarser than a paragraph, but "this chapter changed" is legible and true.
 */
export function segmentDocHtml(source: string): string[] {
  const text = source.replace(/\r\n/g, "\n");
  const blocks: string[] = [];
  const chapterRe = /<chapter\b/gi;
  const starts: number[] = [];
  let match: RegExpExecArray | null;
  while ((match = chapterRe.exec(text))) starts.push(match.index);
  if (starts.length === 0) return [text.trim()].filter(Boolean);
  const head = text.slice(0, starts[0]).trim();
  if (head) blocks.push(head);
  starts.forEach((start, index) => {
    const end = index + 1 < starts.length ? starts[index + 1] : text.length;
    const chunk = text.slice(start, end).trim();
    if (chunk) blocks.push(chunk);
  });
  return blocks;
}

export function segmentMd(md: string): string[] {
  if (isDocHtml(md)) return segmentDocHtml(md);
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const blocks: string[] = [];
  let buffer: string[] = [];
  let mode: "none" | "para" | "list" | "table" | "fence" | "fold" | "quote" = "none";
  const flush = () => {
    if (buffer.length) { blocks.push(buffer.join("\n")); buffer = []; }
    mode = "none";
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (mode === "fold") { buffer.push(line); if (/^\?\?\?\s*$/.test(line)) flush(); continue; }
    if (mode === "fence") { buffer.push(line); if (/^:::\s*$/.test(line)) flush(); continue; }
    if (/^\?\?\?\s+.+$/.test(line)) { flush(); mode = "fold"; buffer.push(line); continue; }
    if (/^:::\s*\w+/.test(line)) { flush(); mode = "fence"; buffer.push(line); continue; }
    if (!line.trim()) { flush(); continue; }
    if (/^#{1,4}\s/.test(line) || /^!!!\s+\w+\s/.test(line) || /^\*{3,}/.test(line)) { flush(); blocks.push(line); continue; }
    if (/^>\s?/.test(line)) { if (mode !== "quote") flush(); mode = "quote"; buffer.push(line); continue; }
    if (/^\s*\|/.test(line)) { if (mode !== "table") flush(); mode = "table"; buffer.push(line); continue; }
    if (/^\s*[-*]\s+/.test(line) || /^\s*\d+[.)]\s+/.test(line)) { if (mode !== "list") flush(); mode = "list"; buffer.push(line); continue; }
    if (mode !== "para") flush();
    mode = "para"; buffer.push(line);
  }
  flush();
  return blocks;
}

function lcsOps(oldBlocks: string[], newBlocks: string[]): DiffOp[] {
  const n = oldBlocks.length;
  const m = newBlocks.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = oldBlocks[i] === newBlocks[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (oldBlocks[i] === newBlocks[j]) { ops.push({ type: "same", block: newBlocks[j] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push({ type: "del", block: oldBlocks[i] }); i++; }
    else { ops.push({ type: "add", block: newBlocks[j] }); j++; }
  }
  while (i < n) ops.push({ type: "del", block: oldBlocks[i++] });
  while (j < m) ops.push({ type: "add", block: newBlocks[j++] });
  return ops;
}

function plainWords(text: string): string[] {
  return text.replace(/\*/g, "").replace(/==/g, "").replace(/\{\.lead\}/g, "").split(/\s+/).filter(Boolean);
}

// Token overlap 0..1; decides whether a removed+added pair is a REWRITE of
// the same block (worth an inline word diff) or two unrelated blocks.
export function similarity(a: string, b: string): number {
  const setA = new Set(plainWords(a.toLowerCase()));
  const setB = new Set(plainWords(b.toLowerCase()));
  if (!setA.size || !setB.size) return 0;
  let common = 0;
  for (const word of setA) if (setB.has(word)) common++;
  return common / Math.max(setA.size, setB.size);
}

function isProse(block: string): boolean {
  return block.split("\n").every((line) =>
    line.trim()
    && !/^#{1,4}\s/.test(line)
    && !/^!!!/.test(line)
    && !/^>/.test(line)
    && !/^\*{3,}/.test(line)
    && !/^\s*\|/.test(line)
    && !/^\s*[-*]\s+/.test(line)
    && !/^\s*\d+[.)]\s+/.test(line)
    && !/^:::/.test(line)
    && !/^\?\?\?/.test(line));
}

export type WordSeg = { type: "same" | "ins" | "del"; text: string };

export function wordDiff(oldText: string, newText: string): WordSeg[] {
  const a = plainWords(oldText);
  const b = plainWords(newText);
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const segs: WordSeg[] = [];
  const push = (type: WordSeg["type"], word: string) => {
    const last = segs[segs.length - 1];
    if (last && last.type === type) last.text += ` ${word}`;
    else segs.push({ type, text: word });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { push("same", a[i]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { push("del", a[i]); i++; }
    else { push("ins", b[j]); j++; }
  }
  while (i < n) push("del", a[i++]);
  while (j < m) push("ins", b[j++]);
  return segs;
}

// Full pipeline: segment both versions, LCS at block level, then pair each
// removed+added window into rewrites where the overlap is convincing.
export function diffBlocks(oldMd: string, newMd: string): DiffOp[] {
  const raw = lcsOps(segmentMd(oldMd), segmentMd(newMd));
  const out: DiffOp[] = [];
  let k = 0;
  while (k < raw.length) {
    const op = raw[k];
    if (op.type === "same") { out.push(op); k++; continue; }
    const dels: string[] = [];
    const adds: string[] = [];
    while (k < raw.length && raw[k].type !== "same") {
      const item = raw[k];
      if (item.type === "del") dels.push(item.block);
      else if (item.type === "add") adds.push(item.block);
      k++;
    }
    const pairCount = Math.min(dels.length, adds.length);
    for (let p = 0; p < pairCount; p++) {
      if (similarity(dels[p], adds[p]) >= 0.3) {
        out.push({ type: "change", oldBlock: dels[p], newBlock: adds[p], inline: isProse(dels[p]) && isProse(adds[p]) });
      } else {
        out.push({ type: "del", block: dels[p] });
        out.push({ type: "add", block: adds[p] });
      }
    }
    for (let p = pairCount; p < dels.length; p++) out.push({ type: "del", block: dels[p] });
    for (let p = pairCount; p < adds.length; p++) out.push({ type: "add", block: adds[p] });
  }
  return out;
}

function countHeadings(md: string): number {
  return md.split("\n").filter((line) => /^#{1,2}\s/.test(line.trimEnd())).length;
}

// Reader body in "what changed" mode. Section numbering follows the CURRENT
// document (removed blocks never advance the counter).
export function DocDiffView(props: { previous: string; current: string; legend: string }) {
  const ops = diffBlocks(props.previous, props.current);
  // Fragments of an HTML document no longer start with <doc>, so the
  // renderer has to be told what it is looking at.
  const html = isDocHtml(props.current) || isDocHtml(props.previous);
  // ...and a chapter-sized fragment cannot see the document's segment legend
  // either, so a diff would print `s1` where the reader saw "Seznam". Built
  // from the CURRENT version: that is the document the reader is comparing
  // against, and a segment the new version dropped no longer needs a colour.
  const docContext = html ? buildDocContext(props.current) : undefined;
  const readable = (block: string): string => (html ? docPlainText(parseDocHtml(block)) : block);
  const out: ReactNode[] = [];
  let buffer: string[] = [];
  let section = 0;
  const flushSame = () => {
    if (!buffer.length) return;
    const md = buffer.join("\n\n");
    out.push(<MarkdownView key={`same-${out.length}`} md={md} sectionStart={section} docHtml={html} docContext={docContext} />);
    section += countHeadings(md);
    buffer = [];
  };
  for (const op of ops) {
    if (op.type === "same") { buffer.push(op.block); continue; }
    flushSame();
    if (op.type === "add") {
      out.push(<div key={`add-${out.length}`} className="doc-diff-add"><MarkdownView md={op.block} sectionStart={section} docHtml={html} docContext={docContext} /></div>);
      section += countHeadings(op.block);
    } else if (op.type === "del") {
      out.push(<div key={`del-${out.length}`} className="doc-diff-del"><MarkdownView md={op.block} sectionStart={section} docHtml={html} docContext={docContext} /></div>);
    } else if (op.inline) {
      // 🔴 A component block is diffed as READING TEXT, never as source. The
      // word diff welds two versions of the same attribute together, so raw
      // markup here reached the partner as `<chapter title="Velka
      // title="Nosna myslenka"` and read as a generation bug (Tomas Pohl,
      // 2026-09-02). Markdown blocks are already reading text and pass
      // through untouched.
      out.push(
        <p key={`chg-${out.length}`} className="doc-diff-change">
          {wordDiff(readable(op.oldBlock), readable(op.newBlock)).map((seg, index) => (
            <Fragment key={index}>
              {index > 0 ? " " : ""}
              {seg.type === "same" ? seg.text : seg.type === "ins" ? <ins>{seg.text}</ins> : <del>{seg.text}</del>}
            </Fragment>
          ))}
        </p>
      );
    } else {
      out.push(<div key={`del-${out.length}`} className="doc-diff-del"><MarkdownView md={op.oldBlock} sectionStart={section} docHtml={html} docContext={docContext} /></div>);
      out.push(<div key={`add-${out.length}`} className="doc-diff-add"><MarkdownView md={op.newBlock} sectionStart={section} docHtml={html} docContext={docContext} /></div>);
      section += countHeadings(op.newBlock);
    }
  }
  flushSame();
  return (
    <div className="doc-diff">
      <p className="doc-diff-legend">{props.legend}</p>
      {out}
    </div>
  );
}
